/**
 * Consentimento de cookies/telemetria (LGPD).
 *
 * Fonte única de verdade para saber se o usuário autorizou Rollbar + PostHog.
 * Módulo puro (só localStorage) — sem `import.meta` — para ser testável e
 * importável em qualquer contexto (inclusive Jest).
 */
export type ConsentValue = 'accepted' | 'rejected';

const CONSENT_KEY = 'aride_cookie_consent';
const listeners = new Set<(value: ConsentValue) => void>();

/** Decisão persistida do usuário, ou `null` se ainda não decidiu. */
export function getStoredConsent(): ConsentValue | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

/** Se o usuário já tomou uma decisão (aceitar/recusar) — usado para exibir o banner. */
export function hasDecidedConsent(): boolean {
  return getStoredConsent() !== null;
}

/** Consentimento para telemetria (Rollbar + PostHog). Só `true` quando aceito. */
export function hasAnalyticsConsent(): boolean {
  return getStoredConsent() === 'accepted';
}

/** Registra a decisão e notifica os assinantes (ex.: App liga/desliga a telemetria). */
export function setConsent(value: ConsentValue): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* noop */
  }
  listeners.forEach((fn) => fn(value));
}

/** Assina mudanças de consentimento; retorna a função de cancelamento. */
export function onConsentChange(fn: (value: ConsentValue) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
